import React from 'react';
import { Box, IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { FeedbackRating } from '@libs/types';
import { ThumbsDownIcon, ThumbsUpIcon } from '@phosphor-icons/react';

interface FeedbackButtonsProps {
  messageId: string;
  currentFeedback?: FeedbackRating;
  onFeedback: (messageId: string, rating: FeedbackRating) => void;
  isSubmitting?: boolean;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = observer(
  ({ messageId, currentFeedback, onFeedback, isSubmitting = false }: FeedbackButtonsProps) => {
    const handleLike = (): void => {
      if (isSubmitting) return;
      const newRating = currentFeedback === 'like' ? null : 'like';
      onFeedback(messageId, newRating);
    };

    const handleDislike = (): void => {
      if (isSubmitting) return;
      const newRating = currentFeedback === 'dislike' ? null : 'dislike';
      onFeedback(messageId, newRating);
    };

    const buttonSx = (isActive: boolean) => ({
      width: 32,
      height: 32,
      borderRadius: '10px',
      color: isActive ? 'primary.main' : 'text.secondary',
      backgroundColor: isActive ? 'primary.light' : '#F5F5F5',
      transition: 'background-color .16s ease, color .16s ease, transform .12s ease',
      '&:hover': {
        backgroundColor: isActive ? 'primary.light' : '#EAEAEA',
        color: isActive ? 'primary.main' : 'text.primary',
      },
      '&:active': { transform: 'scale(0.9)' },
    });

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <IconButton
          size="small"
          onClick={handleLike}
          disabled={isSubmitting}
          sx={buttonSx(currentFeedback === 'like')}
        >
          <ThumbsUpIcon size={16} weight={currentFeedback === 'like' ? 'fill' : 'regular'} />
        </IconButton>

        <IconButton
          size="small"
          onClick={handleDislike}
          disabled={isSubmitting}
          sx={buttonSx(currentFeedback === 'dislike')}
        >
          <ThumbsDownIcon size={16} weight={currentFeedback === 'dislike' ? 'fill' : 'regular'} />
        </IconButton>
      </Box>
    );
  },
);
